<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Historie ulic do roku 1900</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/historie_ulic_do_roku_1900.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Vpád Pasovských na Malou Stranu 15. 2. 1611 na mědirytu
                        z roku 1612, v legendě jsou kromě označení budov pod
                        čísly 15, 17, 18 a 19 též označeny již názvy ulic. /
                        Zdroj: Archiv hlavního města Prahy, Sbírka grafiky,
                        sign. G 1590</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Jak vznikala jména pražských ulic? Tento poutavý text
                    provádí čtenáře vývojem uliční sítě Prahy od nejstarších
                    stezek směřujících k brodům, přes středověké zakládání měst,
                    barokní přestavby i jazykové zápasy 19. století. Od
                    bezejmenných cest až po zavedení oficiálních názvů a čísel
                    popisných – sledujeme nejen urbanistický vývoj, ale i
                    proměnu jazyka a identity města.
                </p>

                <p class="font-bold">Nejstarší doba</p>
                <p class="mb-5">
                    Nejstaršími komunikacemi na území Prahy byly cesty a stezky
                    směřující k brodům v oblasti dnešního Karlova mostu, Klárova
                    a ostrova Štvanice. Tyto cesty byly patrně bezejmenné,
                    stejně jako ty, které od 9. či 10. století v poněkud
                    změněných trasách pod ochranou nově založeného Pražského
                    hradu a Vyšehradu směřovaly do podhradí obou pevností a
                    odtud k dřevěnému mostu, který byl v polovině 12. století
                    nahrazen kamenným mostem
                    <span class="italic">Juditiným. </span>
                    Jméno mostu je jedním z nejstarších známých názvů veřejné
                    komunikace na pražském území. V dochovaných písemných
                    pramenech mu předchází patrně pouze zmínka kronikáře Kosmy o
                    <span class="italic">„ulici Vyšehradské“</span>, vztahující
                    se k roku 1091. Český termín ulice je tu však jen vžitým
                    převodem Kosmova latinského pojmu vicus, který lze přeložit
                    též jako osadu, tržní místo i čtvrť.
                </p>

                <p class="font-bold">Do roku 1348</p>
                <p class="mb-5">
                    Městská uliční síť v dnešním smyslu vznikla teprve poté, co
                    byly původní rozptýlené osady románského pražského podhradí
                    postupně pohlceny zahušťující se souvislou zástavbou a
                    posléze obehnány hradbami i právně sjednoceny a „vysazeny“
                    jako město. To se odehrálo záhy po roce 1230 na pravém
                    vltavském břehu a vzniklo zde rozlehlé dvojměstí Většího
                    (Starého) a Havelského Města. Na místě starého levobřežního
                    podhradí založil král Přemysl Otakar II. roku 1257 Nové
                    Město, pro něž se po sto letech, po založení velkého Nového
                    Města Karlem IV., ujal název Menší Město a později Malá
                    Strana. Ještě před Karlovým zakladatelským aktem z roku
                    1348, jímž se završilo utváření středověkého pražského
                    souměstí, vzniklo na přelomu třicátých a čtyřicátých let 14.
                    století v předpolí královského sídla malé město Hradčany.
                </p>

                <p class="font-bold">Založení Nového Města</p>
                <p class="mb-5">
                    Po založení Nového Města pražského (1348) došlo logicky
                    jednorázově k hromadnému pojmenování jeho ulic (<span
                        class="italic"
                        >Řeznická, Senovážná, Zlatnická, Žitná, Jindřišská,
                        Kateřinská, Ke Karlovu, Klimentská, Na Slovanech,
                        Petrské náměstí, Charvátova, Ve Smečkách, Na slupi, V
                        jámě
                    </span>
                    a mnoho dalších). Nové Město také poskytuje příklad
                    nejstaršího dobového výkladu uličního názvu v pověsti o
                    Nekázance jako o městské komunikaci vzniklé svévolně mimo
                    původní zastavovací plán Karla IV. Řada ulic na území Nového
                    Města ovšem vznikla teprve při parcelaci a zastavování
                    volných zahrad a vinic v jeho hradbách na přelomu 19. a 20.
                    století (<span class="italic"
                        >Albertov, Hálkova, Na Děkance, Studničkova
                    </span>
                    – původně
                    <span class="italic"> Preslova, V Tůních </span>
                    ad.).
                </p>

                <p class="font-bold">15. století</p>
                <p class="mb-5">
                    V první čtvrtině 15. století se pojmenování počeštila a
                    ustálila natolik, že u řady názvů lze doložit jejich trvání
                    až do přítomnosti. Je to případ především pojmenování podle
                    sortimentu zboží, se kterým se na daném prostranství
                    obchodovalo nebo které se zde vyrábělo (<span class="italic"
                        >Celetná, Kostečná, Kožná, Platnéřská, Provaznická,
                        Uhelný trh
                    </span>

                    ad.). Dále sem patří mnoho prostranství a ulic nazvaných
                    podle kostela, kláštera, popřípadě významného sekulárního
                    veřejného objektu, který se zde nacházel nebo ke kterému
                    komunikace směřovala (<span class="italic"
                        >Benediktská, Haštalská, Mariánské náměstí, Martinská,
                        Na Františku, Týnská, Valentinská, Maltézské náměstí,
                        Radnická – Radnické schody</span
                    >). Četné jsou i případy, kdy se ulice začaly nazývat podle
                    význačných domů v nich stojících. Ty byly označovány buď
                    podle významných majitelů, nebo podle svých domovních
                    znamení (<span class="italic"
                        >Jáchymova, Kaprova, Liliová, Rámová, Štupartská,
                        Vejvodova</span
                    >). Konečně do této staré názvoslovné vrstvy patří též
                    některé názvy podle vzhledu či charakteru ulice (<span
                        class="italic"
                        >Dlouhá, Pohořelec, Kočičí</span
                    >).
                </p>

                <p class="font-bold">16. století</p>
                <p class="mb-5">
                    Po požárech (1419, 1420, 1541) byla znovu vybudována takřka
                    celá Malá Strana a v letech 1591–1604 byl celý její městský
                    areál podstatně zvětšen parcelací a zástavbou vykoupených
                    zahrad Zejdlicovské, Lavínovské a dalších na severním úpatí
                    Petřína. Usadili se zde především příslušníci italské
                    menšiny, působící ve službách císaře Rudolfa II., a rovněž
                    jejich Vlašský špitál (1601) a papežská nunciatura (1604).
                    Hlavní ulice nového sídelního okrsku proto dostala jméno

                    <span class="italic"> Vlašská. </span>
                </p>

                <p class="font-bold">17. století</p>
                <p class="mb-5">
                    V 17. století představovala pražská města již raně barokní
                    velkoměsto, které díky dlouholeté přítomnosti císařského
                    dvora Rudolfa II. a mnohostranným zájmům tohoto panovníka
                    patřilo k nejživějším a kulturně nejpřitažlivějším v
                    tehdejší Evropě. Pražská uliční síť se tehdy dotvořila do
                    podoby, ve které v podstatě přetrvala do velkých asanací
                    přelomu 19. a 20. století. Názvosloví veřejných prostranství
                    v pražských městech naproti tomu nedospělo do žádné výsledné
                    podoby, nepočítáme-li jeho úplné počeštění. Bylo nadále v
                    celém rozsahu zvykové a podléhalo živelným procesům, které
                    se nikdo nepokoušel regulovat. Proto byla jeho proměnlivost
                    značná. I staré zavedené a vžité názvy podléhaly posunům
                    (např.
                    <span class="italic">
                        V růžové zahradě – Růžená – Růžová; Smečky – Ve
                        Smečkách; Radnická – Radnické schody; Křemencova –
                        Skřemenná
                    </span>
                    ale současně též
                    <span class="italic"> Poštovská </span>
                    ap.).
                </p>

                <p class="font-bold">18. století</p>
                <p class="mb-5">
                    Již od sklonku 17. věku se objevovaly první projevy
                    poněmčování uličního názvosloví, zprvu na Malé Straně a
                    Hradčanech, v průběhu 18. století pak též na Starém a Novém
                    Městě. Přestože obě pravobřežní města oficiálně úřadovala a
                    vedla hlavní úřední knihy česky až do sedmdesátých let 18.
                    století, do vydání osvícenských císařských dekretů,
                    nařizujících užívání němčiny jako jednotného státního
                    úředního jazyka, množily se již dlouho předtím překlady
                    dosavadních uličních názvů do němčiny a jejich užívání v
                    tomto jazyce. Nicméně až do osvícenských reforem státní a
                    městské správy v poslední třetině 18. století se v Praze
                    nesetkáváme s žádným pokusem o úřední zásah do pojmenování
                    veřejných prostranství. Teprve po zavedení domovních
                    popisných čísel dvorským dekretem z 1. října 1770 a zejména
                    po vytvoření jednotného královského hlavního města Prahy
                    spojením čtyř dosud samostatných pražských měst (Starého
                    Města, Nového Města, Malé Strany a Hradčan) na základě
                    panovnického dekretu z 12. února 1784 se plně ukázala
                    nepostradatelnost názvů veřejných prostranství pro
                    jednoznačnou orientaci ve městě. Záhy poté následovala prvá
                    nařízení o úřední registraci pojmenování pražských ulic a
                    náměstí i o jejich trvalém a viditelném označení. Samotné
                    označování černými nápisy na nárožních domech pak bylo
                    oficiálně zahájeno 27. října 1787. Nejstaršími doposud
                    dochovanými nápisy jsou označení ulic
                    <span class="italic">Jiřská</span>
                    na Pražském hradě a
                    <span class="italic">Dominikánská </span>
                    v dnešní Harantově ulici na Malé Straně.
                </p>

                <p class="font-bold">19. století</p>
                <p class="mb-5">
                    Revoluční rok 1848 přinesl do záměrného (úředního)
                    pojmenovávání veřejných prostranství snahu prosadit v Praze
                    znovu jazykově české, národně vlastenecké i všeslovanské
                    názvy. Prvním podnětem tu byl návrh Karla Havlíčka
                    Borovského ze 16. března 1848, aby na počest národního
                    patrona a na paměť schůze ve Svatováclavských lázních byl
                    Koňský trh nazván
                    <span class="italic">Václavským náměstím. </span>
                    Vzápětí následovalo přejmenování Jezuitské ulice na
                    <span class="italic">Karlovu,</span>
                    Dobytčího trhu na
                    <span class="italic">Karlovo náměstí </span>
                    a záhy poté snaha o opětovné prosazení a užívání původních
                    starých českých názvů.
                </p>

                <p class="font-bold">První zásahy městské samosprávy</p>
                <p class="mb-5">
                    Městská samospráva se rozhodla zasáhnout do uličního
                    názvosloví až v letech 1868–1870. Stalo se tak v souvislosti
                    se zaváděním nového systému číslování domů podle
                    jednotlivých ulic, který byl v celorakouském měřítku
                    přikázán již císařským nařízením z 23. března 1857. Sbor
                    obecních starších se dne 8. října 1868 usnesl, aby současně
                    s označením domů uličními (orientačními) čísly byly také
                    pražské ulice jednotně označeny připevněnými plechovými
                    tabulemi s uličními názvy. Tabulky měly mít v každé čtvrti
                    města odlišnou barvu a měly být německo-české. Mělo být
                    odstraněno opakování stejných uličních názvů. Ustavená
                    komise v tomto duchu vypracovala návrhy nových jmen ulic a
                    náměstí, které postupně projednala a schválila městská rada.
                    Výsledek celé kampaně na její pokyn zaznamenal městský
                    archivář Karel Jaromír Erben ve svém
                    <span class="italic">Autentickém ukazateli.</span>
                </p>

                <p class="font-bold">
                    Jazykové a národnostní spory na konci 19. století
                </p>
                <p class="mb-5 sm:mb-25">
                    Utváření uličního názvosloví nebylo zdaleka bezkonfliktní.
                    Jeho postupné počešťování vyvolalo pobouření německé
                    menšiny, pouliční nacionalistické šarvátky a vzájemné
                    poškozování nových tabulek českých i starých německých a
                    dvojjazyčných. V jejich průběhu dospěl sbor obecních
                    starších 18. dubna 1894 k definitivnímu rozhodnutí označit
                    všechna veřejná prostranství v Praze pouze českými názvy.
                    Již 20. dubna vydala městská rada příslušnou prováděcí
                    vyhlášku. Její součástí byl úřední seznam názvů ulic a
                    náměstí v českém jazyce. Vyhláška stanovila též vnější
                    úpravu uličních tabulek (bílé písmo a modré či bílé ozdobné
                    rámování v červeném poli) a nařizovala majitelům domů
                    odstranit tabule odlišné od nové normy, tj. stará označení
                    německá a německo-česká. Své stanovisko pražská obecní
                    správa postupně obhájila před všemi správními a soudními
                    instancemi a konečným výrokem správního soudu z 1. října
                    1898 spor o uliční názvy v české metropoli skončil. Bylo
                    potvrzeno, že uliční názvosloví je součástí přirozené
                    správní působnosti obcí a Praze bylo přiznáno stejné právo,
                    jaké měla ostatní města rakouské říše, tedy pojmenovávat svá
                    veřejná prostranství jednojazyčným způsobem.
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <!-- nothing -->
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/historie-ulic-1900-1989"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2"
                                >Historie ulic 1900–1989</span
                            >
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
